/**
 * Suscripción al live score (`GET /api/matches/:id/live`, SSE).
 *
 * ## Decisión: fetch-stream, no EventSource
 *
 * `EventSource` no permite mandar headers, y el API exige
 * `Authorization: Bearer <token>` cuando `SCOUT_API_TOKEN` está seteado (no
 * hay token por query string: ver `apps/api/src/app.ts`). Las opciones eran:
 *
 *  a) usar `EventSource` y documentar que el vivo sólo anda con el API en modo
 *     dev abierto;
 *  b) leer el stream con `fetch` + `ReadableStream` y parsear SSE a mano.
 *
 * Elegimos (b): un mismo camino para federación (con token) y cancha (sin
 * token), sin una funcionalidad que se cae justo en producción. El costo es
 * parsear el protocolo —30 líneas, puras y testeables— y reconectar a mano
 * (`EventSource` reconecta solo), que igual queríamos controlar con backoff.
 *
 * CORS: el API responde `Access-Control-Allow-Origin` y acepta el header
 * `authorization`, así que el portal puede vivir en otro dominio.
 */
import type { ScoutEvent } from '@scout/core';
import { getT, type TFunction } from '../i18n/index';

// ---------------------------------------------------------------------------
// Parser SSE (puro)
// ---------------------------------------------------------------------------

export interface SseMessage {
  /** `event:` del bloque; `'message'` si no vino (default del protocolo). */
  event: string;
  /** `data:` concatenada con saltos de línea. */
  data: string;
}

/**
 * Acumula texto y emite un mensaje por cada bloque terminado en línea vacía.
 * Ignora comentarios (`: hb`, el heartbeat del API) y campos desconocidos.
 */
export class SseParser {
  private buffer = '';
  private event = '';
  private data: string[] = [];

  push(chunk: string): SseMessage[] {
    this.buffer += chunk.replace(/\r\n?/g, '\n');
    const out: SseMessage[] = [];
    let index = this.buffer.indexOf('\n');
    while (index !== -1) {
      const line = this.buffer.slice(0, index);
      this.buffer = this.buffer.slice(index + 1);
      const message = this.consumeLine(line);
      if (message) out.push(message);
      index = this.buffer.indexOf('\n');
    }
    return out;
  }

  private consumeLine(line: string): SseMessage | null {
    // Línea vacía: se despacha lo acumulado.
    if (line === '') {
      if (this.data.length === 0) {
        this.event = '';
        return null;
      }
      const message: SseMessage = {
        event: this.event === '' ? 'message' : this.event,
        data: this.data.join('\n'),
      };
      this.event = '';
      this.data = [];
      return message;
    }
    if (line.startsWith(':')) return null; // comentario / heartbeat

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') this.event = value;
    else if (field === 'data') this.data.push(value);
    return null;
  }
}

/** Backoff exponencial acotado: 1s, 2s, 4s, 8s, 15s, 15s… */
export function backoffDelay(attempt: number, baseMs = 1000, maxMs = 15_000): number {
  if (attempt <= 0) return 0;
  const delay = baseMs * 2 ** (attempt - 1);
  return Math.min(delay, maxMs);
}

/** El snapshot inicial del API: desde dónde ponerse al día. */
export interface LiveSnapshotInfo {
  matchId: string;
  lastSeq: number;
}

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed' | 'error';

export interface LiveHandlers {
  /** Llega al conectar (y en cada reconexión): pedí `?sinceSeq=lastSeq` para cubrir el hueco. */
  onSnapshot?(info: LiveSnapshotInfo): void;
  onEvent?(event: ScoutEvent): void;
  onStatus?(status: LiveStatus, detail?: string): void;
  /**
   * Mensaje SSE CRUDO, antes de interpretarlo como evento canónico.
   *
   * Existe porque el stream del portal público no manda eventos del log sino
   * el marcador ya proyectado (`event: score`, ver `apps/api/src/public/`), y
   * eso no entra por `onEvent` —no tiene `id` ni `type`— ni tiene por qué:
   * al widget de overlay le llega un tablero, no el partido. Reusar el parser
   * y la reconexión con backoff que ya están acá es todo el punto; duplicarlos
   * para un consumidor con otro payload habría sido la copia número dos del
   * protocolo.
   */
  onMessage?(message: SseMessage): void;
}

export interface LiveOptions extends LiveHandlers {
  /** URL absoluta del endpoint SSE. */
  url: string;
  /** Headers de auth (los arma el `ApiClient`). */
  headers?: Record<string, string>;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
  /** Inyectable para testear la espera sin dormir de verdad. */
  sleep?: (ms: number) => Promise<void>;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Conexión viva con reconexión automática. `stop()` es definitivo (aborta el
 * fetch en curso y no reintenta): lo llama el `useEffect` al desmontar.
 */
export class LiveSubscription {
  private controller: AbortController | null = null;
  private stopped = false;
  private attempt = 0;

  constructor(private readonly options: LiveOptions) {}

  start(): void {
    if (this.stopped) return;
    void this.loop();
  }

  stop(): void {
    this.stopped = true;
    this.controller?.abort();
    this.controller = null;
    this.options.onStatus?.('closed');
  }

  private async loop(): Promise<void> {
    const sleep = this.options.sleep ?? defaultSleep;
    while (!this.stopped) {
      if (this.attempt > 0) {
        this.options.onStatus?.('reconnecting');
        await sleep(backoffDelay(this.attempt, this.options.baseDelayMs, this.options.maxDelayMs));
        if (this.stopped) return;
      }
      try {
        await this.connect();
        // El server cerró limpio (fin de partido, reinicio): se reintenta.
        this.attempt += 1;
      } catch (err) {
        if (this.stopped) return;
        const message = err instanceof Error ? err.message : String(err);
        // Token malo o partido inexistente: reintentar no lo arregla.
        if (err instanceof FatalLiveError) {
          this.options.onStatus?.('error', message);
          return;
        }
        this.attempt += 1;
        this.options.onStatus?.('error', message);
      }
    }
  }

  private async connect(): Promise<void> {
    const t = getT();
    const fetchImpl = this.options.fetchImpl ?? (globalThis.fetch as LiveOptions['fetchImpl']);
    if (!fetchImpl) throw new FatalLiveError(t('live.noFetch'));

    this.options.onStatus?.(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const controller = new AbortController();
    this.controller = controller;

    const response = await fetchImpl(this.options.url, {
      headers: { accept: 'text/event-stream', ...(this.options.headers ?? {}) },
      signal: controller.signal,
    });

    if (!response.ok) {
      const detail = t('live.openFailed', { status: response.status });
      // 403 va acá igual que el 401: que al rol le falte un permiso no se
      // arregla reintentando, y sin esto el stream reintenta con backoff para
      // siempre. Hoy /live pide `matches:read` (lo tienen todos los roles),
      // pero el día que pida más, el portal no puede quedar en loop.
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404 ||
        response.status === 400
      ) {
        throw new FatalLiveError(detail);
      }
      throw new Error(detail);
    }
    if (!response.body) throw new FatalLiveError(t('live.noStreamBody'));

    this.attempt = 0;
    this.options.onStatus?.('open');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done || this.stopped) break;
        for (const message of parser.push(decoder.decode(value, { stream: true }))) {
          this.dispatch(message);
        }
      }
    } finally {
      reader.cancel().catch(() => undefined);
    }
  }

  private dispatch(message: SseMessage): void {
    // El crudo primero: quien mira el stream público lo consume así.
    this.options.onMessage?.(message);
    const payload = safeJson(message.data);
    if (payload === null) return;
    if (message.event === 'snapshot') {
      const info = payload as Partial<LiveSnapshotInfo>;
      if (typeof info.matchId === 'string' && typeof info.lastSeq === 'number') {
        this.options.onSnapshot?.({ matchId: info.matchId, lastSeq: info.lastSeq });
      }
      return;
    }
    const event = payload as Partial<ScoutEvent>;
    if (typeof event.id === 'string' && typeof event.type === 'string') {
      this.options.onEvent?.(payload as ScoutEvent);
    }
  }
}

/** Error que NO se reintenta (401, 404, entorno sin fetch). */
export class FatalLiveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FatalLiveError';
  }
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/** Texto del estado (idioma activo), para el indicador de la pestaña En vivo. */
export function liveStatusLabel(status: LiveStatus, t: TFunction = getT()): string {
  switch (status) {
    case 'idle':
      return t('live.statusIdle');
    case 'connecting':
      return t('live.statusConnecting');
    case 'open':
      return t('live.statusOpen');
    case 'reconnecting':
      return t('live.statusReconnecting');
    case 'closed':
      return t('live.statusClosed');
    case 'error':
      return t('live.statusError');
  }
}
